import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent } from '@shopify/events';

const SECTION_ID = 'cart-drawer-section';
const DURATION = 10 * 60 * 1000;
const STORAGE_KEY = 'cl-cart-timer-end';

let memoryEnd = 0;

/**
 * Returns the timestamp at which the 10 minute timer ends.
 * Starts a fresh 10 minutes when there is none yet or the previous one ran out.
 */
function getTimerEnd() {
  const now = Date.now();
  let end = 0;

  try {
    end = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
  } catch (_) {
    end = memoryEnd;
  }

  if (!end || end <= now) {
    end = now + DURATION;
    memoryEnd = end;
    try {
      localStorage.setItem(STORAGE_KEY, String(end));
    } catch (_) {
      /* storage unavailable */
    }
  }

  return end;
}

function formatTime(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = String(Math.floor(total / 60)).padStart(2, '0');
  const seconds = String(total % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function tickTimer() {
  const targets = document.querySelectorAll('[data-cl-timer-time]');
  if (!targets.length) return;
  const text = formatTime(getTimerEnd() - Date.now());
  targets.forEach((el) => {
    if (el.textContent !== text) el.textContent = text;
  });
}

function updateUpsellPrice(select) {
  const item = select.closest('[data-cl-up-item]');
  const option = select.options[select.selectedIndex];
  if (!item || !option) return;
  const price = item.querySelector('[data-cl-up-price]');
  const compare = item.querySelector('[data-cl-up-compare]');
  if (price) price.textContent = option.dataset.price || '';
  if (compare) {
    compare.textContent = option.dataset.compare || '';
    compare.hidden = !option.dataset.compare;
  }
}

async function addToCart(button) {
  const item = button.closest('[data-cl-up-item]');
  if (!item) return;
  const select = item.querySelector('[data-cl-up-select]');
  const variantId = select ? select.value : item.dataset.variantId;
  if (!variantId) return;

  const label = button.textContent;
  button.disabled = true;
  button.textContent = '...';

  try {
    const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: SECTION_ID,
        sections_url: window.location.pathname,
      }),
    });
    const json = await response.json();
    if (!response.ok || json.status) throw new Error(json.description || json.message || 'Add to cart failed');

    const sectionHtml = json.sections && json.sections[SECTION_ID];
    let notified = false;

    try {
      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cartJson = await cartResponse.json();
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
          promise: deferred.promise,
        })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson),
        detail: {
          sections: json.sections,
          items: json.items,
          itemCount: cartJson.item_count,
          source: 'cl-cart-upsell',
          didError: false,
        },
      });
      notified = true;
    } catch (error) {
      console.warn('[cl-cart] Could not dispatch cart event', error);
    }

    if (!notified && sectionHtml) {
      await morphSection(SECTION_ID, sectionHtml, { mode: 'hydration' });
    }
  } catch (error) {
    console.error('[cl-cart] Upsell add failed', error);
    button.disabled = false;
    button.textContent = label;
  }
}

if (!window.__clCartExtrasLoaded) {
  window.__clCartExtrasLoaded = true;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const nav = target.closest('[data-cl-up-prev], [data-cl-up-next]');
    if (nav) {
      const track = nav.closest('[data-cl-upsell]')?.querySelector('[data-cl-up-track]');
      if (track) {
        const direction = nav.hasAttribute('data-cl-up-next') ? 1 : -1;
        track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
      }
      return;
    }

    const add = target.closest('[data-cl-up-add]');
    if (add instanceof HTMLButtonElement) {
      event.preventDefault();
      addToCart(add);
    }
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target instanceof HTMLSelectElement && target.hasAttribute('data-cl-up-select')) {
      updateUpsellPrice(target);
    }
  });

  tickTimer();
  setInterval(tickTimer, 1000);
}
